import type { MdsOption } from '@medpacesoftwaredevelopment/designsystem';
import { PaymentRequestStatus, RequestStatusNames, TravelRequestStatus } from '@models/request';
import { RequestType } from '../enums/request-types.enum';
import {
    BaseAccessibleRequestsOptionValue,
    GetAllAccessibleRequestsOptionValue,
} from '../models/all-requests/get-all-accessible-requests-option-value';

type NumericEnum = Record<string, number | string>;

export function resolveRequestStatusName(type: string, statusId: number, basicView: boolean): string {
    switch (type.toLowerCase() as RequestType) {
        case RequestType.Stipend:
        case RequestType.Reimbursement:
            return resolvePaymentStatusName(statusId, basicView);
        case RequestType.Travel:
            return resolveTravelStatusName(statusId, basicView);
        default:
            return '';
    }
}

export function createRequestStatusOptions(basicView: boolean): MdsOption<GetAllAccessibleRequestsOptionValue>[] {
    const optionsByStatusName = new Map<string, MdsOption<GetAllAccessibleRequestsOptionValue>>();

    addStatusOptions(
        optionsByStatusName,
        getNumericEnumValues(PaymentRequestStatus),
        'paymentStatusIds',
        resolvePaymentStatusName,
        basicView
    );
    addStatusOptions(
        optionsByStatusName,
        getNumericEnumValues(TravelRequestStatus),
        'travelStatusIds',
        resolveTravelStatusName,
        basicView
    );

    return Array.from(optionsByStatusName.values());
}

function addStatusOptions(
    optionsByStatusName: Map<string, MdsOption<GetAllAccessibleRequestsOptionValue>>,
    statusIds: readonly number[],
    requestStatusIdsKey: keyof BaseAccessibleRequestsOptionValue,
    resolveStatusName: (statusId: number, basicView: boolean) => string,
    basicView: boolean
): void {
    statusIds.forEach((statusId) => {
        const viewValue = resolveStatusName(statusId, basicView);
        const option = optionsByStatusName.get(viewValue) ?? {
            value: {
                statusName: viewValue,
                paymentStatusIds: [],
                travelStatusIds: [],
            },
            viewValue: viewValue,
        };

        option.value[requestStatusIdsKey] = [...(option.value[requestStatusIdsKey] ?? []), statusId];
        optionsByStatusName.set(viewValue, option);
    });
}

function getNumericEnumValues(statusEnum: NumericEnum): number[] {
    return Object.values(statusEnum).filter((value): value is number => typeof value === 'number');
}

function resolvePaymentStatusName(statusId: number, basicView: boolean): string {
    if (basicView) {
        switch (statusId) {
            case PaymentRequestStatus.PaymentInProcess:
            case PaymentRequestStatus.PendingPSApproval:
            case PaymentRequestStatus.PayoutIssue:
                return RequestStatusNames.PaymentInProcess;
            case PaymentRequestStatus.PendingERSubmission:
            case PaymentRequestStatus.ERSubmitted:
                return RequestStatusNames.Completed;
            case PaymentRequestStatus.PQProcessing:
                return RequestStatusNames.PaymentInProcess;
        }
    }
    return RequestStatusNames[PaymentRequestStatus[statusId]];
}

function resolveTravelStatusName(statusId: number, basicView: boolean): string {
    if (basicView) {
        switch (statusId) {
            case TravelRequestStatus.PendingInvoice:
            case TravelRequestStatus.PendingERSubmission:
            case TravelRequestStatus.ERSubmitted:
                return RequestStatusNames.Completed;
        }
    }
    return RequestStatusNames[TravelRequestStatus[statusId]];
}
